import {FormControl, InputLabel, Input, FormHelperText} from '@material-ui/core';
import React, {useState} from 'react';
import {getUiOptions} from '../../utils';
import {FieldProps, Options} from '../EmailExtension/schema';
import {useIsRepoSlugValid} from '../BitbucketRepoNameValidatorExtension/useIsRepoSlugValid';
import {slugify} from '../utils/bitbucketUtilities';
import {UiSchema} from '@rjsf/utils';

const PROJECT_NAME_PATTERN = /^[a-z0-9]+[-_.|a-z0-9]*[a-z0-9]*$/;

/*
Extension field to allow validation of project name against valid repo slugs
*/

export const ProjectNameValidatorField: React.FC<FieldProps> = ({
  formData,
  rawErrors,
  onChange,
  uiSchema,
}) => {
  const [repoSlug, setRepoSlug] = useState('ENTER A PROJECT NAME');
  const [projectName, setProjectName] = useState(formData ?? '');
  const [validationMessage, setValidationMessage] = useState('');
  const validateRepoSlug = useIsRepoSlugValid();
  const {title, description} = getUiOptions<Options>(uiSchema as UiSchema);
  const hasInvalidProjectName =
    projectName.length > 0 && (!PROJECT_NAME_PATTERN.test(projectName) || /\s/.test(projectName));

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setProjectName(value);
    onChange(value);

    if (value.length === 0) {
      setValidationMessage('');
      return;
    }

    if (!PROJECT_NAME_PATTERN.test(value)) {
      setValidationMessage('❌ Invalid project name. Lowercase letters, numbers, and -_.| only.');
      return;
    }

    const repoSlugValue = slugify(value);
    setRepoSlug(repoSlugValue);
    const isValid = await validateRepoSlug(repoSlugValue);
    if (isValid) {
      setValidationMessage('✅ Repo name is valid.');
    } else {
      setValidationMessage('❌ Invalid characters or a repo with this name already exists.');
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setProjectName(value);
    onChange(value);
    setRepoSlug(slugify(value));

    if (value.length > 0 && !PROJECT_NAME_PATTERN.test(value)) {
      setValidationMessage('❌ Invalid project name. Lowercase letters, numbers, and -_.| only.');
      return;
    }

    setValidationMessage('');
  };

  return (
    <FormControl error={(rawErrors?.length ?? 0) > 0 || hasInvalidProjectName}>
      <InputLabel>{title}</InputLabel>
      <Input
        value={projectName}
        onChange={handleChange}
        onBlur={handleBlur}
        inputProps={{pattern: '^[a-z0-9]+[-_.|a-z0-9]*[a-z0-9]*$', title: 'No spaces allowed'}}
      />
      <FormHelperText>Repo Slug will be: {repoSlug}</FormHelperText>
      {/* describes the input */}
      <FormHelperText>{description}</FormHelperText>
      {/* shows if the project name is valid to be created in git */}
      <FormHelperText>{validationMessage}</FormHelperText>
    </FormControl>
  );
};
